<script setup lang="ts">
import { i18n } from '@/i18n';
import SettingsPageTitle from '@/settingsModule/components/SettingsPageTitle.vue';
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import {
  DashboardAlertDirections,
  DashboardAlertSeverities,
  DashboardAlertSetting,
  DashboardAlertSettingValueType,
  DashboardAlertSeverity,
  DashboardAlertDirection,
  dashboardAlertSettingValidation,
  DEFAULT_DASHBOARD_ALERT_SETTING,
  DashboardAlertSettingValueTypes,
} from '../domains/entities/DashboardAlert';
import AlertInputConsecutive from '../components/alertInput/AlertInputConsecutive.vue';
import AlertInputSharp from '../components/alertInput/AlertInputSharp.vue';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { DashboardAlertSettingFactory } from '../domains/factories/DashboardAlertSettingFactory';
import { diContainer } from '@/coreContainer/container';
import { waitLoading } from '@/corePagesModule/components/InfoModal/waitLoading';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';
import ErrorMessage from '@/corePagesModule/components/ErrorMessage.vue';
import { useRouter } from 'vue-router';
import { simpleCopy } from '@/utils/deepCopy';
import AlertInputCurrent from '../components/alertInput/AlertInputCurrent.vue';

const t = i18n.t;
const router = useRouter();

const text = {
  excess: {
    title: '過剰アラートの設定',
    critical: {
      consecutive: { key: 'dashboard:アラート条件:nヶ月連続でm%以下', option: { n: 2, m: 50 } },
      sharp: { key: 'dashboard:アラート条件:前回受領内示比n%以下かつ差分数量m以上', option: { n: 50, m: 100 } },
    },
    warning: {
      consecutive: { key: 'dashboard:アラート条件:nヶ月連続でm%以下', option: { n: 2, m: 80 } },
      sharp: { key: 'dashboard:アラート条件:前回受領内示比n%以下かつ差分数量m以上', option: { n: 75, m: 100 } },
    },
  },
  shortage: {
    title: '不足アラートの設定',
    critical: {
      consecutive: { key: 'dashboard:アラート条件:nヶ月連続でm%以上', option: { n: 2, m: 150 } },
      sharp: { key: 'dashboard:アラート条件:前回受領内示比n%以上かつ差分数量m以上', option: { n: 200, m: 100 } },
      current: { key: 'dashboard:アラート条件:内示に対して当月の出荷済み数量がn%を超える', option: { n: 100 } },
    },
    warning: {
      consecutive: { key: 'dashboard:アラート条件:nヶ月連続でm%以上', option: { n: 2, m: 120 } },
      sharp: { key: 'dashboard:アラート条件:前回受領内示比n%以上かつ差分数量m以上', option: { n: 125, m: 100 } },
    },
  },
};

/**
 * デフォルト値表示用の条件文を生成する
 * @param key - 条件文の翻訳キー
 * @param option - 条件文へ埋め込むデフォルト値
 */
const defaultValueString = (key: string, option: { n: number; m?: number }) => t('dashboard:デフォルト', { condition: t(key, option) });

const inputValues = ref<DashboardAlertSetting>(DashboardAlertSettingFactory.create());
const isEdited = ref(false);
const errorMessage = ref<DashboardAlertSetting>(DashboardAlertSettingFactory.create());
const bySeverityDirectionErrorMessage = computed(() =>
  Object.fromEntries(
    DashboardAlertSeverities.map((severity) => [
      severity,
      Object.fromEntries(
        DashboardAlertDirections.map((direction) => [
          direction,
          Object.values(errorMessage.value[severity][direction]).filter((s) => s !== '')[0],
        ])
      ),
    ])
  )
);
const isError = computed(() => Object.values(bySeverityDirectionErrorMessage.value).some((dv) => Object.values(dv).some((es) => es)));

/**
 * Stateをリセットする
 */
const resetState = () => {
  inputValues.value = DashboardAlertSettingFactory.create();
  errorMessage.value = DashboardAlertSettingFactory.create();
  isEdited.value = false;
};

/**
 * データフェッチ
 */
const fetch = async () => {
  const res = await waitLoading(diContainer.getDashboardAlertSettingUseCase.execute(), undefined, '');
  if (!res) {
    await showInfoModal({
      icon: 'error',
      modalTitle: t('取得に失敗しました'),
      modalDescription: t('情報の取得に失敗しました。リロードしてください。'),
      backButtonLabel: 'OK',
      isDisabledNextButton: true,
    });
    return;
  }
  switch (res.status.type) {
    case 'success':
      inputValues.value = res.status.data;
      return;
    default:
      await showInfoModal({
        icon: 'error',
        modalTitle: t('取得に失敗しました'),
        modalDescription: t('情報の取得に失敗しました。リロードしてください。'),
        backButtonLabel: 'OK',
        isDisabledNextButton: true,
      });
  }
};

/**
 * Inputハンドラ
 * @param e - Inputイベント
 * @param severity - 危険度
 * @param direction - 向き
 * @param valueType - 値種類
 */
const onInput = async (
  e: Event,
  severity: DashboardAlertSeverity,
  direction: DashboardAlertDirection,
  valueType: DashboardAlertSettingValueType
) => {
  isEdited.value = true;
  const x = (e.target as HTMLInputElement).value;
  inputValues.value[severity][direction][valueType] = x;
  errorMessage.value[severity][direction][valueType] = dashboardAlertSettingValidation(severity, direction, x, valueType);
};

/**
 * 保存ハンドラ
 */
const onClickSave = async () => {
  for (const severity of DashboardAlertSeverities) {
    for (const direction of DashboardAlertDirections) {
      for (const valueType of DashboardAlertSettingValueTypes) {
        errorMessage.value[severity][direction][valueType] = dashboardAlertSettingValidation(
          severity,
          direction,
          inputValues.value[severity][direction][valueType],
          valueType
        );
      }
    }
  }
  await nextTick();
  if (isError.value) {
    return;
  }
  await save();
};

/**
 * リセットハンドラ
 */
const onReset = async () => {
  const confirmReset = await showInfoModal({
    icon: 'info',
    modalTitle: t('デフォルトの設定に戻してよろしいですか？（このまま保存されます）'),
    backButtonLabel: 'キャンセル',
    nextButtonLabel: 'OK',
    noDescription: true,
  });
  if (!confirmReset) return;
  inputValues.value = simpleCopy(DEFAULT_DASHBOARD_ALERT_SETTING);
  await save();
};

/**
 * 保存
 */
const save = async () => {
  const result = await waitLoading(diContainer.putDashboardAlertSettingUseCase.execute(inputValues.value));
  if (!result?.status.type || result.status.type === 'error') {
    showInfoModal({
      icon: 'error',
      colorStyle: 'error',
      modalTitle: t('保存に失敗しました'),
      modalDescription: t('時間をおいて再度お試しください。'),
      backButtonLabel: 'OK',
      isDisabledNextButton: true,
    });
    return;
  }
  const wantBackToAlertPage = await showInfoModal({
    modalTitle: t('保存しました'),
    modalDescription: t('ダッシュボードに戻りますか？'),
    backButtonLabel: t('キャンセル'),
    nextButtonLabel: t('ダッシュボードに戻る'),
  });
  if (wantBackToAlertPage) {
    router.push('/inventory-dashboard');
  } else {
    resetState();
    await fetch();
  }
};

onMounted(async () => {
  await fetch();
});

onBeforeUnmount(() => {
  resetState();
});
</script>
<template>
  <section class="setting-dashboard-alert-page">
    <header class="setting-dashboard-alert-page__header">
      <SettingsPageTitle :label="t('設定 > 発注・計算ロジック > ダッシュボードアラート設定')" />
    </header>
    <div v-for="direction in DashboardAlertDirections" :key="direction" class="setting-dashboard-alert-page__container">
      <div class="container__title">
        {{ t(text[direction].title) }}
      </div>
      <div class="setting">
        <div v-for="severity in DashboardAlertSeverities" :key="severity" class="setting__input">
          <div :class="`input__container input__container--${severity}`">
            <div class="input__title">
              <span>{{ t(`dashboard:${severity}`) }}</span>
              <ErrorMessage
                :is-error="!!bySeverityDirectionErrorMessage[severity][direction]"
                :error-message="bySeverityDirectionErrorMessage[severity][direction]"
              />
            </div>
            <div class="input__items">
              <AlertInputSharp
                :severity="severity"
                :direction="direction"
                :mom-change-rate-threshold="inputValues[severity][direction].momChangeRateThreshold"
                :min-quantity-threshold="inputValues[severity][direction].minQuantityThreshold"
                :default-value-string="defaultValueString(text[direction][severity].sharp.key, text[direction][severity].sharp.option)"
                @update:mom-change-rate-threshold="(e) => onInput(e, severity, direction, 'momChangeRateThreshold')"
                @update:min-quantity-threshold="(e) => onInput(e, severity, direction, 'minQuantityThreshold')"
              />
              <AlertInputConsecutive
                :direction="direction"
                :consecutive-months="inputValues[severity][direction].consecutiveMonths"
                :ratio-threshold="inputValues[severity][direction].ratioThreshold"
                :default-value-string="
                  defaultValueString(text[direction][severity].consecutive.key, text[direction][severity].consecutive.option)
                "
                @update:consecutive-months="(e) => onInput(e, severity, direction, 'consecutiveMonths')"
                @update:ratio-threshold="(e) => onInput(e, severity, direction, 'ratioThreshold')"
              />
              <AlertInputCurrent
                v-if="severity === 'critical' && direction === 'shortage'"
                :threshold="inputValues[severity][direction].currentMonthForecastAccuracyThreshold"
                :default-value-string="defaultValueString(text[direction][severity].current.key, text[direction][severity].current.option)"
                @update:threshold="(e) => onInput(e, severity, direction, 'currentMonthForecastAccuracyThreshold')"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="button__container">
      <TextButton :disabled="!isEdited || isError" @click="onClickSave">{{ t('保存') }}</TextButton>
      <button class="button--back" @click="onReset">{{ t('デフォルトに戻す') }}</button>
    </div>
  </section>
</template>
<style lang="scss" scoped>
.setting-dashboard-alert-page {
  display: grid;
  gap: 24px;
  padding: 24px;

  &__header {
    display: grid;
    gap: 0;
  }
  &__container {
    background-color: white;
    padding: 24px;
    border-radius: 8px;
    display: grid;
    gap: 16px;
    grid-template-columns: 1fr;
  }
}

.container {
  &__title {
    font-size: 18px;
    font-weight: 700;
  }
}

.setting {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  &__input {
    padding-right: 16px;
    &:not(:last-child) {
      border-right: 2px solid $borderDefault;
    }
  }
}

.input {
  &__container {
    padding: 16px;
    border-radius: 4px;
    &--critical {
      background-color: $semanticErrorSubdued;
    }
    &--warning {
      background-color: $semanticWarningSubdued;
    }
  }
  &__title {
    display: grid;
    grid-template-columns: max-content 1fr;
    align-items: center;
    gap: 8px;
    font-size: 18px;
    font-weight: 700;
  }
  &__items {
    display: grid;
    grid-template-columns: repeat(2, auto);
  }
}

.button__container {
  display: grid;
  grid-template-columns: max-content max-content;
  gap: 16px;
}

.button {
  &--back {
    font-weight: 700;
  }
}
</style>
